import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { CelumPropertiesProvider } from '@celum/core';
import { BaseFormat, BaseFormatIcon, BaseFormatList, CreateBaseFormat, SearchFormat, UpdateBaseFormat } from '@celum/formats/domain';

import { FormatsContextAware } from './formats-context-aware';
import { NotificationService } from './notification.service';

export const BASE_FORMAT_SERVICE = new InjectionToken<BaseFormatsService>('BASE_FORMAT_SERVICE');

/**
 * Manages "base formats" (the global format definitions catalog maintained by CELUM admins in the `formats-embed`
 * "Base formats" administration tab), as opposed to the tenant-scoped formats handled by `FormatsService`.
 *
 * Base formats are a dedicated model (`BaseFormat`, distinct from `Format`) served by their own, non-tenant-scoped
 * endpoints (`/v1/definitions/formats...`), so this is a standalone implementation - it extends `FormatsContextAware`
 * directly (not `FormatsService`) and resolves its own absolute `baseApiUrl`, following the same pattern as
 * `ProcessService`/`AssignmentsService`.
 */
@Injectable()
export class BaseFormatsService extends FormatsContextAware {
  public icons: BaseFormatIcon[] = [];

  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);

  private static readonly LOAD_ERROR_KEY = 'FORMAT_LIST.SNACKBAR.LOAD_ERROR';

  constructor() {
    super();
    this.getFormatIcons().subscribe(icons => this.setIcons(icons));
  }

  private get baseApiUrl(): string {
    return `${CelumPropertiesProvider.properties.appProperties.formats.apiUrl}/definitions`;
  }

  private setIcons(icons: BaseFormatIcon[] | null | undefined): void {
    const resolvedIcons = icons ?? [];
    const defaultIcon = resolvedIcons.find(icon => icon.id === 'default');
    this.icons = defaultIcon ? [defaultIcon, ...resolvedIcons.filter(icon => icon.id !== 'default')] : resolvedIcons;
  }

  public getFormatByKey(key: string): Observable<BaseFormatList> {
    return this.withContext(context =>
      this.http.get<BaseFormatList>(`${this.baseApiUrl}/formats`, { params: { formatKey: key }, context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on getFormatByKey', error.error);
          this.notificationService.error(error.error?.errorMessage || BaseFormatsService.LOAD_ERROR_KEY);
          return of({ content: [], totalElements: 0, hasNext: false } as BaseFormatList);
        })
      )
    );
  }

  public getFormatById(formatId: number): Observable<BaseFormat> {
    return this.withContext(context =>
      this.http.get<BaseFormat>(`${this.baseApiUrl}/formats/${formatId}`, { context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on getFormatById', error.error);
          this.notificationService.error(error.error?.errorMessage || BaseFormatsService.LOAD_ERROR_KEY);
          return of(null);
        })
      )
    );
  }

  public deleteFormat(formatId: number): Observable<void> {
    return this.withContext(context =>
      this.http.delete<void>(`${this.baseApiUrl}/formats/${formatId}`, { context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on deleteFormat', error.error);
          this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.DELETE_ERROR');
          throw error;
        })
      )
    );
  }

  public createFormat(format: CreateBaseFormat): Observable<BaseFormat> {
    return this.withContext(context =>
      this.http.post<BaseFormat>(`${this.baseApiUrl}/formats`, format, { context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on format creation', error.error);
          this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.CREATE_ERROR');
          return of(null);
        })
      )
    );
  }

  public updateFormat(formatId: number, format: UpdateBaseFormat): Observable<BaseFormat> {
    return this.withContext(context =>
      this.http.put<BaseFormat>(`${this.baseApiUrl}/formats/${formatId}`, format, { context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on format update', error.error);
          if (error.error?.errorKey === 'format.only-celum-admin-allowed') {
            this.notificationService.error('FORMAT_LIST.SNACKBAR.PROTECTED');
          } else {
            this.notificationService.error(error.error?.errorMessage || 'FORMAT_LIST.SNACKBAR.UPDATE_ERROR');
          }
          return of(null);
        })
      )
    );
  }

  public searchFormats(search: SearchFormat): Observable<BaseFormatList> {
    return this.withContext(context =>
      this.http.post<BaseFormatList>(`${this.baseApiUrl}/formats/search`, search, { context }).pipe(
        catchError(error => {
          console.error('BaseFormatsService: error on format search', error.error);
          this.notificationService.error(error.error?.errorMessage || BaseFormatsService.LOAD_ERROR_KEY);
          return of({ content: [], totalElements: 0, hasNext: false } as BaseFormatList);
        })
      )
    );
  }

  public getFormatIcons(): Observable<BaseFormatIcon[]> {
    return this.withContext(context =>
      this.http.get<BaseFormatIcon[]>(`${this.baseApiUrl}/formats/icons`, { context }).pipe(
        map(icons => icons ?? []),
        catchError(error => {
          console.error('BaseFormatsService: error when get format icons', error);
          this.notificationService.error(error.error?.errorMessage || BaseFormatsService.LOAD_ERROR_KEY);
          return of([]);
        })
      )
    );
  }
}
